import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { jobsApi, ApiError } from "@/api";
import type { JobListRow } from "@/types";
import { Button, Spinner, Toast, useToast } from "@/components/ui";
import { jobStatusLabel } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import { formatDateShort } from "@/lib/format";
import "./on-hold.css";

/** Jobs › On Hold — every job currently paused (status `on_hold`). These are
 *  hidden from the main jobs list by default (its "Include on hold" toggle shows
 *  them there); this page is their home. Each row resumes with one click:
 *  POST /resume_job re-derives the stage from the job's current scope (the
 *  backend owns the target status — never guessed here), so the row drops off
 *  once it's no longer on hold. */

/** One-line billing summary (invoiced of agreed), matching the archive workbench. */
function billingLine(row: JobListRow): string {
  if (!row.has_billing) return "No fees or invoices";
  if (row.total_agreed <= 0) return `${formatMoney(row.total_invoiced)} invoiced · no fees`;
  return `${formatMoney(row.total_invoiced)} of ${formatMoney(row.total_agreed)} invoiced`;
}

export function OnHoldJobs() {
  const { toast, showToast } = useToast();

  const [rows, setRows] = useState<JobListRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // Per-job in-flight resume, so each button spins independently.
  const [resumingIds, setResumingIds] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    jobsApi
      .listOnHoldJobs()
      .then((items) => {
        if (!cancelled) setRows(items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load on-hold jobs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  /** Drop the resumed job — once it's off hold it no longer belongs on this
   *  page, so removing it is the honest optimistic update. */
  function removeRow(id: number) {
    setRows((prev) => (prev ? prev.filter((row) => row.id !== id) : prev));
  }

  async function resume(row: JobListRow) {
    if (resumingIds.has(row.id)) return;
    setResumingIds((prev) => new Set(prev).add(row.id));
    setError(undefined);
    try {
      const res = await jobsApi.resumeJob(row.id);
      removeRow(row.id);
      showToast(
        `Resumed #${row.job_number}${row.job_name ? ` · ${row.job_name}` : ""} → ${jobStatusLabel(res.job.status)}`,
        "info",
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not resume that job.";
      setError(msg);
      showToast(msg, "warn");
    } finally {
      setResumingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }

  return (
    <section className="onhold-wb">
      <Link to="/staff/jobs" className="onhold-wb__back">← Jobs</Link>

      <header className="onhold-wb__head">
        <div>
          <h1>On hold</h1>
          <p className="onhold-wb__sub">
            {rows ? (
              <>
                {rows.length} job{rows.length === 1 ? "" : "s"} on hold
              </>
            ) : (
              "Jobs paused and hidden from the main list. Resume one to put it back in play."
            )}
          </p>
        </div>
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="onhold-wb__loading">
          <Spinner label="Loading on-hold jobs…" />
        </div>
      ) : !rows || rows.length === 0 ? (
        <p className="onhold-wb__empty">No jobs are on hold right now.</p>
      ) : (
        <div className="onhold-table" role="table" aria-label="On-hold jobs">
          <div className="onhold-table__head" role="row">
            <span role="columnheader">Job #</span>
            <span role="columnheader">Job name</span>
            <span role="columnheader">Client</span>
            <span role="columnheader">Billing</span>
            <span role="columnheader">Last active</span>
            <span role="columnheader" className="onhold-table__action-col">Action</span>
          </div>
          {rows.map((row) => (
            <div key={row.id} className="onhold-table__row" role="row">
              <span role="cell" data-label="Job #" className="onhold-table__ref numeric">
                {row.job_number}
              </span>
              <span role="cell" data-label="Job name">
                <Link to={`/staff/jobs/${row.id}`} className="onhold-table__name-link">
                  {row.job_name || "—"}
                </Link>
              </span>
              <span role="cell" data-label="Client">{row.client_display || "—"}</span>
              <span role="cell" data-label="Billing" className="onhold-table__billing">
                {billingLine(row)}
              </span>
              <span role="cell" data-label="Last active" className="onhold-table__activity">
                {formatDateShort(row.last_activity_at)}
              </span>
              <span role="cell" data-label="Action" className="onhold-table__action-col">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={resumingIds.has(row.id)}
                  disabled={resumingIds.has(row.id)}
                  onClick={() => resume(row)}
                >
                  Resume
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}

      <Toast toast={toast} />
    </section>
  );
}
